/* ==========================================================================
   In8te — product.js (0.0.132)
   Product page components: variant picker, media gallery + lightbox,
   sticky add-to-cart, recommendations. Loaded only on product templates.
   Shared helpers come from window.Theme (set up by theme.js).
   ========================================================================== */

const Theme = window.Theme || { settings: {}, strings: {} };
const money = (cents) => (Theme.formatMoney ? Theme.formatMoney(cents) : cents);
const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

/* ----------------------------- <variant-picker> ---------------------------- */
class VariantPicker extends HTMLElement {
  connectedCallback() {
    const json = this.querySelector('[data-product-json]');
    if (!json) return;
    try { this.product = JSON.parse(json.textContent); } catch { return; }
    // analytics: view_item (theme.js listens; see Analytics)
    setTimeout(() => {
      const variant = this.currentVariant ? this.currentVariant() : null;
      document.dispatchEvent(new CustomEvent('product:view', { detail: { product: this.product, variant } }));
    }, 0);
    this.fieldsets = [...this.querySelectorAll('[data-option-index]')];
    this.priceWrap = this.querySelector('[data-price-wrapper]');
    this.idInput = this.querySelector('[data-variant-id]');
    this.atcButton = this.querySelector('[data-atc-button]');
    this.atcText = this.querySelector('[data-atc-text]');
    this.atcPrice = this.querySelector('[data-atc-price]');

    this.addEventListener('change', (e) => {
      if (e.target.matches('[data-option-input]')) this.onChange();
    });
    this.updateAvailability();
  }

  selectedOptions() {
    return this.fieldsets.map((fs) => fs.querySelector('[data-option-input]:checked')?.value ?? null);
  }

  currentVariant() {
    const selected = this.selectedOptions();
    return this.product.variants.find((v) => v.options.every((val, i) => val === selected[i]));
  }

  onChange() {
    const variant = this.currentVariant();
    this.fieldsets.forEach((fs) => {
      const label = fs.querySelector('[data-option-value]');
      const checked = fs.querySelector('[data-option-input]:checked');
      if (label && checked) label.textContent = checked.value;
    });
    this.updateAvailability();
    this.render(variant);
    document.dispatchEvent(new CustomEvent('variant:change', { detail: { variant } }));
  }

  /* Mark chips that lead to an unavailable/nonexistent variant */
  updateAvailability() {
    const selected = this.selectedOptions();
    this.fieldsets.forEach((fs, optionIndex) => {
      fs.querySelectorAll('[data-option-input]').forEach((input) => {
        const test = [...selected];
        test[optionIndex] = input.value;
        const match = this.product.variants.find((v) => v.options.every((val, i) => test[i] === null || val === test[i]));
        input.closest('label')?.classList.toggle('is-unavailable', !match || !match.available);
      });
    });
  }

  render(variant) {
    if (!variant) {
      if (this.atcButton) { this.atcButton.disabled = true; }
      if (this.atcText) this.atcText.textContent = Theme.strings.unavailable;
      return;
    }
    if (this.idInput) this.idInput.value = variant.id;

    if (this.priceWrap) {
      const onSale = variant.compare_at_price && variant.compare_at_price > variant.price;
      let html = `<span class="price${onSale ? ' price--sale' : ''}"><span class="price__current">${money(variant.price)}</span>`;
      if (onSale) {
        const pct = Math.round(((variant.compare_at_price - variant.price) / variant.compare_at_price) * 100);
        html += `<s class="price__compare">${money(variant.compare_at_price)}</s><span class="price__percent">&minus;${pct}%</span>`;
      }
      html += '</span>';
      const priceEl = this.priceWrap.querySelector('.price');
      if (priceEl) priceEl.outerHTML = html; else this.priceWrap.insertAdjacentHTML('afterbegin', html);
    }

    if (this.atcButton) this.atcButton.disabled = !variant.available;
    if (this.atcText) this.atcText.textContent = variant.available ? Theme.strings.addToCart : Theme.strings.soldOut;
    if (this.atcPrice) this.atcPrice.textContent = money(variant.price);
    document.querySelectorAll('[data-sticky-price]').forEach((el) => { el.textContent = money(variant.price); });
    // keep the sticky bar in sync with the selected variant
    document.querySelectorAll('[data-sticky-atc]').forEach((btn) => {
      btn.disabled = !variant.available;
      btn.textContent = variant.available ? Theme.strings.addToCart : Theme.strings.soldOut;
    });
    const stickyVariant = document.querySelector('[data-sticky-variant]');
    if (stickyVariant) stickyVariant.textContent = variant.title || '';
    // sticky thumbnail follows the variant's photo (color) via the gallery
    if (variant.media_id) {
      const mediaImg = document.querySelector(`.product-gallery__item[data-media-id="${variant.media_id}"] img`);
      const stickyImg = document.querySelector('.sticky-atc__img');
      if (mediaImg && stickyImg) {
        stickyImg.src = mediaImg.currentSrc || mediaImg.src;
        stickyImg.removeAttribute('srcset');
      }
    }

    if (variant.media_id) {
      document.querySelector('product-gallery')?.goToMediaId(variant.media_id);
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', variant.id);
    window.history.replaceState({}, '', url);
  }
}
customElements.define('variant-picker', VariantPicker);

/* ----------------------------- <product-gallery> --------------------------- */
class ProductGallery extends HTMLElement {
  connectedCallback() {
    this.viewport = this.querySelector('[data-gallery-viewport]');
    if (!this.viewport) return;
    this.allItems = [...this.viewport.querySelectorAll('.product-gallery__item')];
    this.allThumbs = [...this.querySelectorAll('[data-thumb]')];
    this.allDots = [...this.querySelectorAll('.product-gallery__dot')];
    this.desktop = window.matchMedia('(min-width: 990px)');
    this.index = 0; // index within VISIBLE items
    this.viewport.classList.add('is-ready');
    this.userActive = false;
    ['pointerdown', 'keydown', 'wheel', 'touchstart'].forEach((ev) => document.addEventListener(ev, () => { this.userActive = true; }, { once: true, passive: true }));

    this.allThumbs.forEach((t) => t.addEventListener('click', () => {
      const item = this.allItems[Number(t.dataset.thumb)];
      const i = this.visItems().indexOf(item);
      if (i >= 0) this.goTo(i);
    }));
    this.querySelector('[data-gallery-prev]')?.addEventListener('click', () => this.goTo(this.index - 1));
    this.querySelector('[data-gallery-next]')?.addEventListener('click', () => this.goTo(this.index + 1));

    // Dots follow the swipe frame by frame (rAF-throttled, no debounce lag)
    let ticking = false;
    this.viewport.addEventListener('scroll', () => {
      if (this.desktop.matches || ticking) return; // desktop switches instantly, no sliding
      ticking = true;
      requestAnimationFrame(() => {
        ticking = false;
        const i = Math.round(this.viewport.scrollLeft / this.itemWidth());
        if (i !== this.index) this.setActive(i);
      });
    }, { passive: true });

    this.desktop.addEventListener('change', () => this.goTo(this.index));

    // Rapha-style: start filtered to the selected color once the variant
    // picker has parsed its data
    setTimeout(() => {
      const vp = document.querySelector('variant-picker');
      const colorIdx = (vp?.product?.colorOption || 0) - 1;
      const variant = colorIdx >= 0 && vp.currentVariant ? vp.currentVariant() : null;
      if (variant) this.filterColor(variant.options[colorIdx]);
      else this.goTo(0);
    }, 0);

    // Zoom lightbox — a full, navigable gallery (images and videos)
    this.lightbox = document.querySelector('[data-gallery-lightbox]');
    this.querySelector('[data-gallery-zoom]')?.addEventListener('click', () => this.openLightbox());
    // Click on a photo OR a video opens the lightbox on THAT item (never on
    // the debounced scroll index, which can lag behind on mobile)
    this.viewport.addEventListener('click', (e) => {
      if (!this.lightbox) return; // zoom switched off in the section settings
      if (!e.target.matches('img.product-gallery__img, video, .product-gallery__video')) return;
      const item = e.target.closest('.product-gallery__item');
      const i = item ? this.visItems().indexOf(item) : -1;
      this.openLightbox(i >= 0 ? i : this.index);
    });
    this.lightbox?.addEventListener('click', (e) => {
      if (e.target.closest('[data-lightbox-close]') || e.target === this.lightbox) this.closeLightbox();
      if (e.target.closest('[data-lightbox-prev]')) this.lightboxGo(-1);
      if (e.target.closest('[data-lightbox-next]')) this.lightboxGo(1);
      const th = e.target.closest('[data-lightbox-thumb]');
      if (th) this.lightboxGoTo(Number(th.dataset.lightboxThumb));
      // click on the photo zooms INTO the clicked spot; second click zooms out
      const zoomImg = e.target.closest('.gallery-lightbox__cell img');
      if (zoomImg) {
        const cell = zoomImg.parentElement;
        if (cell.classList.contains('is-zoomed')) {
          cell.classList.remove('is-zoomed');
          cell.scrollTop = 0;
          cell.scrollLeft = 0;
        } else {
          // relative position of the click on the photo (0..1)
          const rect = zoomImg.getBoundingClientRect();
          const rx = (e.clientX - rect.left) / rect.width;
          const ry = (e.clientY - rect.top) / rect.height;
          cell.classList.add('is-zoomed');
          // after the zoomed layout, center that same spot in the pan area
          const zoomed = zoomImg.getBoundingClientRect();
          const cellRect = cell.getBoundingClientRect();
          const imgLeft = zoomed.left - cellRect.left + cell.scrollLeft;
          const imgTop = zoomed.top - cellRect.top + cell.scrollTop;
          cell.scrollLeft = imgLeft + rx * zoomed.width - cell.clientWidth / 2;
          cell.scrollTop = imgTop + ry * zoomed.height - cell.clientHeight / 2;
        }
      }
    });
    this.onLightboxKey = (e) => {
      if (e.key === 'Escape') this.closeLightbox();
      if (e.key === 'ArrowLeft') this.lightboxGo(-1);
      if (e.key === 'ArrowRight') this.lightboxGo(1);
    };
    const lbWrap = this.lightbox?.querySelector('[data-lightbox-viewport]');
    let lbTimer;
    lbWrap?.addEventListener('scroll', () => {
      clearTimeout(lbTimer);
      lbTimer = setTimeout(() => this.syncLightbox(), 80);
    }, { passive: true });

    // Videos autoplay (muted) as soon as they are the current slide and the
    // gallery is on screen; everything else stays paused
    this.inViewItems = new Set();
    const io = new IntersectionObserver((entries) => {
      entries.forEach((en) => { if (en.isIntersecting) this.inViewItems.add(en.target); else this.inViewItems.delete(en.target); });
      this.syncVideos();
    }, { threshold: 0.35 });
    this.allItems.forEach((el) => io.observe(el));
  }

  syncVideos() {
    const current = this.visItems()[this.index];
    this.allItems.forEach((el) => {
      const v = el.querySelector('video');
      if (!v) return;
      // grid: every visible video on screen plays; carousel: the current slide only
      const wanted = this.desktop.matches ? !el.hidden && this.inViewItems.has(el) : el === current && this.inViewItems.has(el);
      if (wanted && !reducedMotion.matches) v.play().catch(() => {});
      else v.pause();
    });
  }

  /* currently visible items/thumbs (color filtering hides the rest) */
  visItems() { return this.allItems.filter((el) => !el.hidden); }
  visThumbs() { return this.allThumbs.filter((el) => !el.hidden); }

  /* Show only the selected color's photos; unmapped photos show for every
     color, and an unmapped color falls back to showing everything */
  filterColor(color) {
    if (this._filterColor === color) return; // size changes must not reset the gallery
    this._filterColor = color;
    const anyMatch = this.allItems.some((el) => el.dataset.color && el.dataset.color === color);
    const show = (el) => !anyMatch || !el.dataset.color || el.dataset.color === color;
    this.allItems.forEach((el) => { el.hidden = !show(el); });
    this.allThumbs.forEach((el) => {
      const item = this.allItems[Number(el.dataset.thumb)];
      el.hidden = item ? item.hidden : false;
    });
    this.allDots.forEach((el, n) => { el.hidden = this.allItems[n] ? this.allItems[n].hidden : false; });
    this.index = 0;
    this.goTo(0);
  }

  /* Slide width + gap — on mobile slides are 84% wide so the next photo peeks */
  itemWidth() {
    const item = this.visItems()[0];
    if (!item) return this.viewport.clientWidth || 1;
    const gap = parseFloat(getComputedStyle(this.viewport).columnGap || 0) || 0;
    return item.getBoundingClientRect().width + gap;
  }

  goTo(i) {
    const items = this.visItems();
    if (!items.length) return;
    const next = Math.max(0, Math.min(i, items.length - 1));
    if (this.desktop.matches) {
      // desktop grid: every photo is already on the page, nothing to switch
    } else {
      const target = items[next];
      this.viewport.scrollTo({
        left: target.offsetLeft - items[0].offsetLeft,
        behavior: reducedMotion.matches ? 'auto' : 'smooth',
      });
    }
    this.setActive(next);
  }

  goToMediaId(id) {
    const item = this.allItems.find((el) => Number(el.dataset.mediaId) === Number(id));
    if (!item || item.hidden) return;
    const i = this.visItems().indexOf(item);
    if (i < 0) return;
    if (this.desktop.matches) {
      // grid: bring the variant's photo into view — but only after the
      // visitor has done something; never move the page on load
      if (!this.userActive) return;
      const r = item.getBoundingClientRect();
      const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 64;
      if (r.top < header || r.bottom > window.innerHeight) {
        item.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'center' });
      }
      this.setActive(i);
      return;
    }
    this.goTo(i);
  }

  setActive(i) {
    this.index = i;
    this.visThumbs().forEach((t, n) => {
      t.classList.toggle('is-active', n === i);
      t.setAttribute('aria-selected', n === i ? 'true' : 'false');
      if (n === i) {
        // scroll only the thumb column — scrollIntoView would also scroll
        // the page itself
        const col = t.parentElement;
        if (col && col.scrollHeight > col.clientHeight) {
          const delta = t.getBoundingClientRect().top - col.getBoundingClientRect().top;
          col.scrollTo({
            top: col.scrollTop + delta - col.clientHeight / 2 + t.clientHeight / 2,
            behavior: reducedMotion.matches ? 'auto' : 'smooth',
          });
        }
      }
    });
    this.allDots.filter((d) => !d.hidden).forEach((d, n) => d.classList.toggle('is-active', n === i));
    this.syncVideos();
  }

  openLightbox(startIndex = this.index) {
    if (!this.lightbox) return;
    const wrap = this.lightbox.querySelector('[data-lightbox-viewport]');
    wrap.innerHTML = '';
    const thumbs = this.lightbox.querySelector('[data-lightbox-thumbs]');
    if (thumbs) thumbs.innerHTML = '';
    // Every media type gets a cell: images as large images, uploaded videos
    // as playable <video>. Unknown/external media are skipped safely.
    this.visItems().forEach((item) => {
      const video = item.querySelector('video');
      const img = item.querySelector('img');
      let el = null;
      if (video) {
        el = document.createElement('video');
        el.controls = true;
        el.muted = true;
        el.playsInline = true;
        el.preload = 'metadata';
        el.loop = true;
        if (video.poster) el.poster = video.poster;
        video.querySelectorAll('source').forEach((s) => el.appendChild(s.cloneNode()));
        if (!el.querySelector('source') && video.currentSrc) el.src = video.currentSrc;
      } else if (img) {
        el = document.createElement('img');
        // full-resolution version regardless of which size the grid loaded
        el.src = (img.currentSrc || img.src).replace(/(width=)\d+/, '$11600');
        el.alt = img.alt || '';
      }
      if (!el) return;
      const cell = document.createElement('div');
      cell.className = 'gallery-lightbox__cell';
      cell.appendChild(el);
      wrap.appendChild(cell);
      // matching thumbnail (same index as the cell)
      if (thumbs) {
        const t = document.createElement('button');
        t.type = 'button';
        t.className = 'gallery-lightbox__thumb';
        t.setAttribute('role', 'tab');
        t.dataset.lightboxThumb = String(wrap.children.length - 1);
        const tsrc = img ? (img.currentSrc || img.src).replace(/(width=)\d+/, '$1240') : (video?.poster || '');
        if (tsrc) { const ti = document.createElement('img'); ti.src = tsrc; ti.alt = ''; ti.loading = 'lazy'; t.appendChild(ti); }
        if (video) t.insertAdjacentHTML('beforeend', '<span class="gallery-lightbox__thumb-play"><svg viewBox="0 0 24 24" width="10" height="10" fill="currentColor" aria-hidden="true"><path d="M8 5v14l11-7z"/></svg></span>');
        thumbs.appendChild(t);
      }
    });
    if (!wrap.children.length) return;
    this.lightbox.hidden = false;
    document.body.classList.add('no-scroll');
    document.addEventListener('keydown', this.onLightboxKey);
    requestAnimationFrame(() => {
      const start = Math.max(0, Math.min(startIndex, wrap.children.length - 1));
      const cell = wrap.children[start];
      wrap.scrollTo({ left: cell ? cell.offsetLeft : start * wrap.clientWidth, behavior: 'auto' });
      this.syncLightbox();
    });
  }

  lightboxGoTo(i) {
    const wrap = this.lightbox?.querySelector('[data-lightbox-viewport]');
    const cell = wrap?.children[i];
    if (!cell) return;
    wrap.scrollTo({ left: cell.offsetLeft, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  }

  lightboxGo(dir) {
    const wrap = this.lightbox?.querySelector('[data-lightbox-viewport]');
    if (!wrap || this.lightbox.hidden) return;
    wrap.scrollBy({ left: dir * wrap.clientWidth, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
  }

  /* Counter + arrow states + pause off-screen videos */
  syncLightbox() {
    const wrap = this.lightbox?.querySelector('[data-lightbox-viewport]');
    if (!wrap || !wrap.children.length) return;
    const i = Math.round(wrap.scrollLeft / wrap.clientWidth);
    const total = wrap.children.length;
    const counter = this.lightbox.querySelector('[data-lightbox-counter]');
    if (counter) counter.textContent = `${Math.min(i + 1, total)} / ${total}`;
    const prev = this.lightbox.querySelector('[data-lightbox-prev]');
    const next = this.lightbox.querySelector('[data-lightbox-next]');
    if (prev) prev.disabled = i <= 0;
    if (next) next.disabled = i >= total - 1;
    const strip = this.lightbox.querySelector('[data-lightbox-thumbs]');
    strip?.querySelectorAll('[data-lightbox-thumb]').forEach((t, n) => {
      const on = n === i;
      t.classList.toggle('is-active', on);
      t.setAttribute('aria-selected', on ? 'true' : 'false');
      if (on && strip.scrollWidth > strip.clientWidth) {
        strip.scrollTo({ left: t.offsetLeft - strip.clientWidth / 2 + t.clientWidth / 2, behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      }
    });
    [...wrap.children].forEach((cell, n) => {
      const v = cell.querySelector('video');
      if (v && n !== i && !v.paused) v.pause();
      // the enlarged video starts playing by itself (muted, with controls)
      if (v && n === i && v.paused && !reducedMotion.matches) v.play().catch(() => {});
      // leaving a slide resets its zoom
      if (n !== i && cell.classList.contains('is-zoomed')) {
        cell.classList.remove('is-zoomed');
        cell.scrollTop = 0;
        cell.scrollLeft = 0;
      }
    });
  }

  closeLightbox() {
    this.lightbox.hidden = true;
    this.lightbox.querySelectorAll('video').forEach((v) => v.pause());
    document.body.classList.remove('no-scroll');
    document.removeEventListener('keydown', this.onLightboxKey);
  }
}
customElements.define('product-gallery', ProductGallery);

/* ----------------------------- Smart sticky buy box ------------------------ */
/* Desktop only. While the buy box fits under the header it is simply sticky.
   When it is taller than the screen, its BOTTOM sticks while scrolling down
   and its TOP sticks while scrolling up (in between it moves with the page),
   so every part of it can be reached and nothing is ever cut off. The mode
   changes only when the panel is exactly where the next mode would put it,
   so it never jumps. */
class StickyPanel {
  constructor(el) {
    this.el = el;
    this.desktop = window.matchMedia('(min-width: 990px)');
    this.mode = 'top';
    this.lastY = window.scrollY;
    window.addEventListener('scroll', () => this.update(false), { passive: true });
    window.addEventListener('resize', () => this.update(true));
    if ('ResizeObserver' in window) new ResizeObserver(() => this.update(true)).observe(el);
    this.desktop.addEventListener('change', () => this.update(true));
    this.update(true);
  }
  topOffset() {
    const header = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 64;
    return header + 20;
  }
  set(mode, top) {
    this.mode = mode;
    this.el.style.position = mode === 'rel' ? 'relative' : 'sticky';
    this.el.style.top = `${Math.round(top)}px`;
  }
  update(force) {
    const el = this.el;
    if (!this.desktop.matches) { el.style.position = ''; el.style.top = ''; this.mode = 'top'; return; }
    const y = window.scrollY;
    const dir = y > this.lastY ? 'down' : y < this.lastY ? 'up' : null;
    this.lastY = y;
    const vh = window.innerHeight;
    const h = el.offsetHeight;
    const topOff = this.topOffset();
    const bottomOff = 24;
    // fits on screen: plain sticky under the header
    if (h + topOff + bottomOff <= vh) { if (force || this.mode !== 'top') this.set('top', topOff); return; }
    if (force) { this.set('top', topOff); return; } // layout changed: start over from the top mode
    if (!dir) return;
    const rect = el.getBoundingClientRect();
    const naturalTop = el.parentElement.getBoundingClientRect().top; // where it sits in the flow
    const bottomTop = vh - h - bottomOff; // "top" that keeps the bottom edge on screen
    if (dir === 'down') {
      if (this.mode === 'top' && rect.top <= topOff + 1) this.set('rel', rect.top - naturalTop);
      else if (this.mode === 'rel' && rect.bottom <= vh - bottomOff + 1) this.set('bottom', bottomTop);
    } else if (this.mode === 'bottom' && rect.top < topOff) {
      this.set('rel', rect.top - naturalTop);
    } else if (this.mode === 'rel' && rect.top >= topOff - 1) {
      this.set('top', topOff);
    }
  }
}
const buyBox = document.querySelector('.product__layout > .product__info');
if (buyBox) new StickyPanel(buyBox);

/* Picking a color re-filters the gallery to that color's photos */
document.addEventListener('variant:change', (e) => {
  const gallery = document.querySelector('product-gallery');
  const vp = document.querySelector('variant-picker');
  const colorIdx = (vp?.product?.colorOption || 0) - 1;
  const variant = e.detail?.variant;
  if (gallery && variant && colorIdx >= 0) gallery.filterColor(variant.options[colorIdx]);
});

/* ----------------------------- <sticky-atc> -------------------------------- */
class StickyAtc extends HTMLElement {
  connectedCallback() {
    const target = document.querySelector('.product__atc');
    if (!target) return;
    this.hidden = false;
    this.classList.remove('is-visible');
    // visible whenever the real buy button is off-screen — including on
    // mobile before the user has scrolled down to it — but never over the
    // footer (currency/language selectors and payment icons must stay reachable)
    this.atcVisible = true;
    this.footerVisible = false;
    const sync = () => this.classList.toggle('is-visible', !this.atcVisible && !this.footerVisible);
    new IntersectionObserver(([en]) => { this.atcVisible = en.isIntersecting; sync(); }, { threshold: 0 }).observe(target);
    const footer = document.querySelector('footer.footer, .footer, footer');
    if (footer) new IntersectionObserver(([en]) => { this.footerVisible = en.isIntersecting; sync(); }, { threshold: 0 }).observe(footer);

    this.querySelector('[data-sticky-atc]')?.addEventListener('click', () => {
      document.getElementById('MainProductForm')?.requestSubmit();
    });
  }
}
customElements.define('sticky-atc', StickyAtc);

/* ----------------------------- Mini upsell add buttons ---------------------- */
document.addEventListener('click', async (e) => {
  const btn = e.target.closest('[data-mini-add]');
  if (!btn) return;
  btn.classList.add('btn--loading');
  try {
    await Theme.Cart.add({ id: Number(btn.dataset.miniAdd), quantity: 1 }, { open: true });
  } catch (err) {
    Theme.toast?.(err.message || Theme.strings.cartError);
  } finally {
    btn.classList.remove('btn--loading');
  }
});

/* ----------------------------- <product-recommendations> -------------------- */
class ProductRecommendations extends HTMLElement {
  connectedCallback() {
    const url = this.dataset.url;
    if (!url) return;
    new IntersectionObserver((entries, observer) => {
      if (!entries[0].isIntersecting) return;
      observer.disconnect();
      fetch(url)
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const fresh = doc.querySelector('product-recommendations');
          if (!fresh || !fresh.querySelector('product-card')) return;
          if (this.dataset.merge === 'true') {
            // Merchant-picked related products stay first; Shopify's
            // automatic ones fill the remaining slots (no duplicates)
            const track = this.querySelector('.carousel__track');
            const limit = parseInt(this.dataset.limit || 8, 10);
            const have = new Set([...this.querySelectorAll('product-card')].map((c) => c.dataset.handle));
            fresh.querySelectorAll('product-card').forEach((card) => {
              if (!track || have.size >= limit || have.has(card.dataset.handle)) return;
              have.add(card.dataset.handle);
              track.appendChild(card);
            });
            this.querySelector('scroll-carousel')?.update?.();
          } else {
            // Swap in real recommendations; otherwise keep the server-rendered fallback
            this.innerHTML = fresh.innerHTML;
          }
          document.dispatchEvent(new CustomEvent('shopify:section:load', { bubbles: true }));
        })
        .catch(() => {});
    }, { rootMargin: '0px 0px 300px 0px' }).observe(this);
  }
}
customElements.define('product-recommendations', ProductRecommendations);
